import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';

import { numberLinks, type NumberedLink } from '../utils/markdownLinks';

interface CitationRegistry {
  /**
   * Numbers every link in `text`, assigning a fresh number to each URL not seen before and
   * reusing the one already held for a URL that has. Called during render, so it must stay
   * idempotent: the same text registered twice returns the same numbers rather than
   * consuming more of them.
   */
  register: (text: string) => NumberedLink[];
  /**
   * The number already held for a URL, without claiming one. A caller that is not prose -
   * a chart beside the paragraph citing it - must not consume a number, or it would take
   * the one belonging to text the reader meets first.
   */
  numberFor: (url: string) => number | undefined;
}

const CitationNumberingContext = createContext<CitationRegistry | null>(null);

/**
 * Fallback for the many `AnalysisMarkdown`s rendered outside a provider (cards, chips, the
 * legacy sections). They number their own links from 1, which is what a lone block should
 * do. A module-level singleton so its `register` identity is stable across renders.
 */
const STANDALONE: CitationRegistry = { register: numberLinks, numberFor: () => undefined };

// eslint-disable-next-line react-refresh/only-export-components -- Hook must be co-located with its provider
export const useCitationNumbering = () => useContext(CitationNumberingContext) ?? STANDALONE;

/**
 * Makes citation numbers run continuously across every `AnalysisMarkdown` inside, in render
 * order, so a reader sees one ascending sequence down the page instead of each block
 * restarting at 1. A URL cited in two blocks keeps the number it was first given.
 *
 * Wrap a whole section rather than a single block. Numbers are handed out as blocks render,
 * so reordering the children renumbers them — which is the intent, since the numbers
 * describe reading order.
 */
export const CitationNumberingProvider = ({ children }: { children: ReactNode }) => {
  // A ref, not state: assignment happens during a child's render, and setting state there
  // would loop. Nothing re-reads this to paint, so there is nothing to re-render for.
  const assigned = useRef(new Map<string, number>());

  const value = useMemo<CitationRegistry>(
    () => ({
      register: (text) =>
        numberLinks(text).map((link) => {
          const existing = assigned.current.get(link.url);
          if (existing !== undefined) return { ...link, number: existing };

          const number = assigned.current.size + 1;
          assigned.current.set(link.url, number);
          return { ...link, number };
        }),
      numberFor: (url) => assigned.current.get(url),
    }),
    [],
  );

  return (
    <CitationNumberingContext.Provider value={value}>{children}</CitationNumberingContext.Provider>
  );
};
